import React, { Ref } from 'react';

import cx from 'classnames';

import { FocusIndicator } from '~shared/components/FocusIndicator';
import { Link } from '~shared/components/Link';
import { Spinner } from '~shared/components/Spinner';

export type UIButtonAppearance = 'default' | 'primary' | 'risky' | 'text';
export type UIButtonType = 'button' | 'submit' | 'reset';
export interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  isDisabled?: boolean;
  isLoading?: boolean;
  type?: UIButtonType;
  onClick?: (e) => void;
  a11yLabel?: string;
  autoFocus?: boolean;
  appearance?: UIButtonAppearance;
  roundedClassName?: string;
  isHovered?: boolean;
  className?: string;
}
type ButtonRef = HTMLButtonElement;

function useClassName({
  roundedClassName,
  isDisabled,
  appearance,
  isLoading,
  isHovered,
  className,
}: {
  roundedClassName: string;
  isDisabled: boolean;
  appearance: string;
  isLoading: boolean;
  isHovered: boolean;
  className: string;
}) {
  // button classname styles
  return React.useMemo(
    () =>
      cx(
        'group relative m-0 transition ease-in-out duration-150',
        'font-display text-display font-600 leading-8 antialiased',
        'border-0 appearance-none outline-none select-none',
        {
          'rounded-2': !roundedClassName,
          'cursor-pointer': !isDisabled,
          'text-disabled':
            isDisabled && ['default', 'text', null].includes(appearance),
          'bg-grey-50': isDisabled && ['default', null].includes(appearance),

          'px-6 py-4': appearance !== 'text',
        },
        isDisabled
          ? 'cursor-not-allowed'
          : isLoading
          ? 'cursor-wait'
          : 'cursor-pointer',

        appearance === 'default' && [
          !isDisabled && 'bg-cyan-50 text-cyan-700 hocus:bg-cyan-100',
          isHovered && 'bg-cyan-100',
        ],

        appearance === 'primary' && [
          'text-white',
          !isDisabled && 'bg-cyan-500 hocus:bg-cyan-600',
          isDisabled && 'bg-disabled text-white',
          isHovered && 'bg-cyan-600',
        ],

        appearance === 'risky' && [
          !isDisabled && 'bg-red-50 text-red-800 hocus:bg-red-100',
        ],

        appearance === 'text' && [
          'px-4 py-2 bg-transparent',
          !isDisabled && 'text-cyan-700 hocus:text-cyan-600',
          isHovered && 'text-cyan-600',
        ],
        className ?? ''
      ),
    [appearance, className, isDisabled, isHovered, isLoading, roundedClassName]
  );
}

export const UIButton = React.forwardRef<ButtonRef, ButtonProps>(
  (
    {
      children,
      isDisabled,
      isLoading,
      onClick,
      a11yLabel,
      autoFocus,
      appearance = 'default',
      type = 'button',
      roundedClassName,
      isHovered,
      className,
      ...rest
    },
    ref
  ) => {
    // the button will limit the onclick action when it's in either the loading or disabled state
    // while this prevents it from firing any events, it is still a good idea to handle that disabled
    // state in the actual callback, if it's an important action (i.e. limiting apis against spam calls)
    const internalOnClickHandler = React.useCallback(
      (e) => {
        if (!onClick) return;

        if (isDisabled || isLoading) return;

        onClick?.(e);
      },
      [isDisabled, isLoading, onClick]
    );

    const computedClassname = useClassName({
      roundedClassName: roundedClassName,
      isDisabled: isDisabled,
      appearance: appearance,
      isLoading: isLoading,
      isHovered: isHovered,
      className: className,
    });
    return (
      <button
        autoFocus={autoFocus}
        aria-label={a11yLabel}
        type={type}
        className={computedClassname}
        onClick={internalOnClickHandler}
        ref={ref}
        data-id={UIButton.displayName}
        disabled={isDisabled}
        role={'button'}
        {...rest}
      >
        {isLoading && (
          <div
            className={
              'absolute top-0 left-0 w-full h-full flex items-center justify-center pointer-events-none'
            }
            style={{ background: 'inherit', zIndex: 2 }}
          >
            <Spinner size={'small'} />
          </div>
        )}
        <FocusIndicator
          notOffset={appearance === 'text'}
          colorClass={appearance === 'risky' ? 'text-red-600' : undefined}
        />
        {children}
      </button>
    );
  }
);
UIButton.displayName = 'UiButton';

export type UITextButtonProps =
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    isDisabled?: boolean;
    isLoading?: boolean;
    type?: UIButtonType;
    autoFocus?: boolean;
    appearance?: UIButtonAppearance;
    roundedClassName?: string;
    isHovered?: boolean;
  };

export const UITextButton = React.forwardRef(function UITextButton(
  {
    roundedClassName,
    isDisabled,
    href,
    appearance,
    isLoading,
    onClick,
    isHovered,
    className,
    children,
    ...rest
  }: UITextButtonProps,
  ref: Ref<HTMLAnchorElement>
) {
  const computedClassName = useClassName({
    roundedClassName: roundedClassName,
    isDisabled: isDisabled,
    appearance: appearance,
    isLoading: isLoading,
    isHovered: isHovered,
    className: className,
  });

  return (
    <Link
      {...rest}
      ref={ref}
      className={cx(computedClassName, 'no-underline')}
      onClick={(event) => {
        if (isDisabled || isLoading) return;
        onClick?.(event);
      }}
      href={href}
    >
      {children}
    </Link>
  );
});
